import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DeviceMobile, QrCode, CircleNotch, Timer, ShieldCheck } from "@phosphor-icons/react";

export default function MobileAccess() {
  const { csrf } = useAuth();
  const [pairing, setPairing] = useState(null);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/devices/pairing/start", {}, { headers: { "X-CSRF-Token": csrf } });
      setPairing(r.data);
      setTimeout(() => setPairing(null), (r.data.expires_in_seconds || 300) * 1000);
    } catch {
      toast.error("Could not start pairing", { description: "Please try again." });
    }
    setBusy(false);
  };

  return (
    <div className="max-w-3xl" data-testid="mobile-access-page">
      <div className="flex items-center gap-2 eyebrow mb-3">
        <DeviceMobile size={14} weight="fill" className="text-primary" /> Mobile PWA
      </div>
      <h1 className="t-page-title">Get Mobile Access</h1>
      <p className="mt-3 t-support text-muted-foreground max-w-xl">
        Add the Rotary GOV Report Builder to a phone — first installation, a replacement phone or an
        additional phone. Scan the QR code with the phone's camera, then create a six-digit
        Rotary Access PIN for that device.
      </p>

      <div className="mt-6 border border-border bg-surface rounded-md p-6 sm:p-8">
        {!pairing ? (
          <div className="text-center">
            <QrCode size={40} weight="duotone" className="text-primary mx-auto" />
            <p className="mt-3 t-support text-muted-foreground">
              A single-use pairing code will be generated. It is valid for <b>5 minutes</b> and
              tied to your signed-in account.
            </p>
            <button
              data-testid="start-pairing-button" onClick={start} disabled={busy}
              className="mt-5 inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold rounded-md hover:bg-primary/90 disabled:opacity-60 transition-colors"
            >
              {busy ? <CircleNotch size={16} className="animate-spin" weight="bold" /> : <QrCode size={16} weight="bold" />}
              Generate pairing QR
            </button>
          </div>
        ) : (
          <div className="text-center" data-testid="pairing-qr">
            <div className="inline-block bg-white p-4 rounded-md border border-border">
              <QRCodeSVG value={pairing.pairing_url} size={220} />
            </div>
            <p className="mt-4 t-support text-muted-foreground flex items-center justify-center gap-2">
              <Timer size={16} weight="duotone" className="text-amber-600" />
              Scan with the phone's camera within 5 minutes. The code is single-use.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              On the phone you will be asked to create a six-digit Rotary Access PIN before
              entering the platform. There is no skip option.
            </p>
          </div>
        )}
      </div>

      <div className="mt-6 border border-border bg-surface rounded-md p-5 flex items-start gap-3">
        <ShieldCheck size={20} weight="duotone" className="text-primary shrink-0 mt-0.5" />
        <p className="t-support text-muted-foreground">
          Manage or revoke phones anytime from <b>Devices and access</b>. Access requires both
          this device's secure pairing and its Rotary Access PIN — never a SIM card or SIM slot.
        </p>
      </div>
    </div>
  );
}
